// ── Early Bird 项目 wiki 式档案报告 ──────────────────────────────────
// 早期/pre-token 项目没有链上数据、没有代币，六维评分体系一大半维度都会显示
// "数据缺失"，套用意义不大（见 2026-07-06 讨论）。改为：创始人/产品/公司/
// 运营数据/竞品/参考来源 六段式档案，其中：
// - 产品/运营数据 两段直接用 earlybird_projects 表里已有的结构化真实字段拼，
//   不需要 AI 猜，也不需要搜索（github_stars/pushed_at/site_alive 等本身就是真数据）
// - 创始人/公司/竞品 三段需要真实背景信息，走搜索 + AI 综合，AI 只能引用喂给它的
//   真实搜索结果（编号引用 [1][2]...），生成后校验引用编号必须落在真实提供的搜索
//   结果范围内，防止编造来源（见 ai.js 已有的反幻觉设计）
//
// 搜索数据源：一开始用 Brave Search，但用户没有 Brave API key（生产环境
// BRAVE_API_KEY 未配置，/api/brave-search 一直 503，导致档案几乎空白，
// 2026-07-06 实测暴露）。改为复用 teamEnhanced.js 里 Layer D 已经在生产验证过的
// 两个零/低成本数据源：DuckDuckGo HTML SERP（免费、不需要 API key）+ Twitter v2
// 搜索（复用已配置的 TWITTER_BEARER_TOKEN）。不新增任何外部依赖或付费项。
//
// 2026-07-09 再补充：DuckDuckGo 这条路后来失效了——实测生产环境 12/12 真实项目查询
// 全部命中 DDG 反自动化拦截页（HTTP 202，canonical 指向 DDG 首页而非搜索结果），
// 换了完全不同网络直连 DDG 复现同样结果，确认是 DDG 最近收紧了对 html.duckduckgo.com
// 的反爬策略，不是某个 IP 被针对性限流。改用 Surf 的 /search/web（真实存在、经过
// 生产环境验证：同一个 "Solomon Labs founder" 查询直接命中创始人 LinkedIn 资料），
// 付费但用户自己的 Surf 账号在用。
//
// 2026-07-07 补充：GitHub README 全文。很多早期项目的 README 里已经写清楚
// 产品/技术/团队/联系方式，是项目方自己写的第一手真实资料，比搜索引擎抓到的
// 二手报道更可信。作为普通"来源"喂给引用式 AI 综合（可被 [n] 引用、也受
// sanitizeCitations 校验），不直接进 buildDeterministicSections——那两段刻意
// 保持"零 AI、零解读"，混入未加工的英文 markdown 会破坏这个不变性。
//
// 2026-07-09 再补充：官网 footer 常见的文档/博客子页面 + LinkedIn 公司页团队信息
// （复用主分析流程已有的 BrightData 代理 fetchLinkedInPeople，付费接口，只在官网
// 自己链接了 LinkedIn 时才触发，避免无谓调用）。链接发现从 fetchHomepageContent
// 返回的完整 compressed 文本里做（见 projectContext.js），不能用截断到 15000 字符
// 的 compressedText——真实项目 footer 常年被截断掉，Twitter 链接偶发漏检就是这个
// 原因，这次一并修了。
//
// 2026-07-09 再补充：真实案例（Solomon Labs）官网是纯前端渲染 SPA，服务端拿到的
// 原始 HTML body 是空的，footer 里的 LinkedIn 链接完全抓不到。加了一层兜底：官网
// 找不到就从 Surf 网页搜索结果里找 linkedin.com/company/ 链接（搜索引擎经常会
// 索引到公司的 LinkedIn 主页，即使官网本身是 JS 渲染的）。
//
// 2026-07-09 再补充：发现的推特账号除了拉推文做情绪/话题分析，现在也拉账号资料本身
// （简介/粉丝数/头像，fetchTwitterProfileSummary，同一个 /api/twitter-bios 接口，只是
// 之前只用了里面的网站字段）。账号发现的候选文本也加了新闻搜索结果——之前只扫了网页
// 搜索摘要，新闻报道里经常带官方推特链接或 @handle 提及，漏掉了这一路。
import { RD_BASE as AI_BASE } from './config';
import { insertMissingCommas, escapeStrayQuotes } from './ai';
import { searchTwitterForTeam, fetchGithubReadme, fetchTopContributors, fetchFounderTweets, analyzeFounderTweets, fetchLinkedInPeople, mapLinkedInEmployeesToFounders, fetchTwitterProfileSummary } from './teamEnhanced';
import { detectPedigreeSignal } from './founderPedigree';
import { fetchHomepageContent } from './projectContext';
import { rdSkill } from './rootdata';
import { getNewsFeed, surfWebSearch } from './surf';
import { resolveSurfProjectName } from './surfNameResolver';
import { supabase } from '../lib/supabase';

function parseGithubOwnerRepo(githubUrl) {
  if (!githubUrl) return null;
  const m = String(githubUrl).match(/github\.com\/([^/\s]+)(?:\/([^/\s?#]+))?/i);
  if (!m || !m[2]) return null;
  return { owner: m[1], repo: m[2].replace(/\.git$/, '') };
}

/** Surf /search/web 单条查询，失败/异常时静默降级为空数组（不让一路失败拖垮整体） */
async function safeWebSearch(q) {
  try {
    const res = await surfWebSearch(q);
    return Array.isArray(res?.data) ? res.data : [];
  } catch { return []; }
}

/** 四组定向网页搜索（创始人/公司/竞品/LinkedIn 公司主页）+ Twitter 搜索 + GitHub README，去重合并。
 *  第 4 组"company linkedin"专门为找公司 LinkedIn 主页服务——实测比指望前三组搜索
 *  顺带搜到更可靠（前三组经常只搜到个人资料/动态帖子，找不到公司主页本身），找到
 *  公司主页才能触发 fetchLinkedInTeamSource 那一路真实团队数据。 */
async function collectSearchResults(projectName, githubUrl) {
  const queries = [
    `${projectName} founder co-founder CEO`,
    `${projectName} company startup funding raises`,
    `${projectName} competitor alternative`,
    `${projectName} company linkedin`,
  ];
  const ghIds = parseGithubOwnerRepo(githubUrl);
  const [webBatches, tweets, readme] = await Promise.all([
    Promise.all(queries.map(q => safeWebSearch(q))),
    searchTwitterForTeam(projectName),
    ghIds ? fetchGithubReadme(ghIds.owner, ghIds.repo) : Promise.resolve(null),
  ]);

  const seen = new Set();
  const merged = [];
  // README 放最前面（项目方第一手资料，信息密度通常最高），且不受下面 15 条上限影响
  if (readme) {
    merged.push({ title: 'GitHub README', url: githubUrl, description: readme });
    seen.add(githubUrl);
  }
  for (const batch of webBatches) {
    for (const r of batch) {
      if (!r.url || seen.has(r.url)) continue;
      seen.add(r.url);
      merged.push({ title: r.title, url: r.url, description: r.description || '' });
    }
  }
  for (const t of (tweets || [])) {
    if (!t.author_username) continue;
    const url = `https://x.com/${t.author_username}`;
    if (seen.has(url)) continue; // 同一作者的推文只作为一条来源引用，避免刷屏式重复
    seen.add(url);
    merged.push({ title: `@${t.author_username}${t.author_verified ? ' ✓' : ''}`, url, description: t.text });
  }
  return merged.slice(0, 15); // 上限 15 条，控制 prompt 长度（README 已在前面，不会被裁掉）
}

function normName(s) {
  return (s || '').toLowerCase().replace(/[^a-z0-9]/g, '');
}

/** RootData 交叉核查：项目是否已被 RootData 收录（免费 ser_inv 搜索，走代理缓存） */
async function fetchRootDataCrossCheck(projectName) {
  try {
    const results = await rdSkill('ser_inv', { query: projectName, precise_x_search: false });
    const list = Array.isArray(results) ? results : (results?.list || results?.data || []);
    const hit = list.find(r => r.type === 1 && normName(r.name || r.project_name) === normName(projectName));
    if (!hit) return null;
    return {
      name: hit.name || hit.project_name || projectName,
      introduce: (hit.introduce || '').slice(0, 400),
      rootdataUrl: hit.rootdataurl || hit.rootdataUrl || '',
    };
  } catch { return null; }
}

/** 项目官网首页内容（复用主分析流程已有的 fetchHomepageContent，此前 Early Bird wiki 只用了 site_alive 布尔值，正文完全没读过） */
async function fetchProjectWebsiteContent(project) {
  if (!project.project_live_url || !project.site_alive) return null;
  return fetchHomepageContent(project.project_live_url).catch(() => null);
}

/** 官网 footer 常见的文档/博客子页面，同样复用 fetchHomepageContent（只抓正文，不做二次跳转） */
async function fetchExtraWebsitePages(links) {
  const [docsContent, blogContent] = await Promise.all([
    links?.docsUrl ? fetchHomepageContent(links.docsUrl).catch(() => null) : Promise.resolve(null),
    links?.blogUrl ? fetchHomepageContent(links.blogUrl).catch(() => null) : Promise.resolve(null),
  ]);
  return { docsContent, blogContent };
}

function extraPageToSource(title, fallbackUrl, content) {
  if (!content) return null;
  const desc = [content.metaDescription, content.compressedText].filter(Boolean).join('\n').slice(0, 2000);
  return { title, url: content.finalUrl || fallbackUrl, description: desc };
}

/** 项目官网 footer 里的 LinkedIn 公司页 → 团队成员列表（复用主分析流程已有的
 *  BrightData 代理 fetchLinkedInPeople，不是自己裸抓；只在官网自己链接了 LinkedIn
 *  时才触发，付费接口，避免无谓调用）。团队信息作为一条来源喂给 AI 综合创始人段落，
 *  和其它补充来源一样受 sanitizeCitations 校验，不直接写死进报告结构。 */
/** @returns {Promise<{ source: object|null, pedigreeHits: Array<{name, type, evidence}> }>} */
async function fetchLinkedInTeamSource(linkedinCompanyUrl) {
  if (!linkedinCompanyUrl) return { source: null, pedigreeHits: [] };
  const result = await fetchLinkedInPeople(linkedinCompanyUrl).catch(() => null);
  if (!result || result.error || result.pending) return { source: null, pedigreeHits: [] };
  const founders = mapLinkedInEmployeesToFounders(result.employees);
  if (!founders.length) return { source: null, pedigreeHits: [] };
  const description = founders.map(f => `${f.name} — ${f.role}${f.bio ? '：' + f.bio : ''}`).join('\n');
  const source = { title: 'LinkedIn 团队信息', url: linkedinCompanyUrl, description };
  // LinkedIn 的 priorWork 是真实履历（"CTO @ Acme 2018-2022"这种），跟 GitHub bio/company
  // 一样喂给同一个 detectPedigreeSignal，只是这次原文换成 bio + priorWork 拼接。
  const pedigreeHits = founders
    .map(f => ({ f, signal: detectPedigreeSignal(f.bio, (f.priorWork || []).join('；')) }))
    .filter(x => x.signal)
    .map(x => ({ name: x.f.name, type: x.signal.type, evidence: x.signal.evidence }));
  return { source, pedigreeHits };
}

/** 项目相关新闻（Surf /news/feed）。Surf 索引对项目名精确匹配要求高，
 *  必须先过 resolveSurfProjectName 找到能命中索引的名称变体，否则大概率 0 条。 */
async function fetchProjectNews(projectName) {
  try {
    const resolved = await resolveSurfProjectName(projectName);
    if (!resolved) return [];
    const res = await getNewsFeed(resolved, 5, 'recency');
    const items = Array.isArray(res?.data) ? res.data : [];
    return items.filter(n => n.title && n.url).slice(0, 5);
  } catch { return []; }
}

function newsToSources(newsItems) {
  return newsItems.map(n => ({
    title: n.title,
    url: n.url,
    description: `${n.source || 'News'}${n.published_at ? ' · ' + new Date(n.published_at * 1000).toISOString().slice(0, 10) : ''}`,
  }));
}

// 从官网/README/搜索摘要文本里找项目官方 Twitter 主页链接，排除明显不是用户名的路径段
const TWITTER_HANDLE_BLOCKLIST = /^(intent|share|home|search|i|hashtag|login|signup)$/i;
function discoverTwitterHandleFromText(text) {
  if (!text) return null;
  const m = String(text).match(/(?:twitter|x)\.com\/([a-zA-Z0-9_]{2,15})(?![a-zA-Z0-9_])/);
  if (!m) return null;
  const handle = m[1];
  return TWITTER_HANDLE_BLOCKLIST.test(handle) ? null : handle;
}

/** 从 Surf 网页搜索结果里找 LinkedIn 公司主页链接，作为官网 footer 发现不到时的兜底——
 *  很多早期项目官网是纯前端渲染 SPA（React/Vite 打包，服务端拿到的原始 HTML 里
 *  body 是空的），footer 里的 LinkedIn 链接抓不到，但网页搜索结果里经常会搜到。 */
function findLinkedInCompanyUrlInSearchResults(searchResults) {
  const hit = searchResults.find(r => /linkedin\.com\/company\//i.test(r.url || ''));
  return hit ? hit.url : null;
}

/** 从候选文本中发现项目官方推特账号，拉账号资料（简介/粉丝数/头像）+ 最近推文并做
 *  AI 深度分析（技术密度/情绪/红绿旗）。账号资料和推文分析两路独立，任一路失败不
 *  影响另一路——有的账号推文拉不到（受保护/长期不发），但资料页本身还是能看的。 */
async function analyzeProjectTwitterAccount(projectName, candidateTexts) {
  let handle = null;
  for (const t of candidateTexts) {
    handle = discoverTwitterHandleFromText(t);
    if (handle) break;
  }
  if (!handle) return null;

  const [tweetsMap, profile] = await Promise.all([
    fetchFounderTweets([handle], 30).catch(() => ({})),
    fetchTwitterProfileSummary(handle).catch(() => null),
  ]);
  const tweets = tweetsMap[handle.toLowerCase()] || [];
  const analysis = tweets.length ? await analyzeFounderTweets(handle, tweets, projectName, projectName).catch(() => null) : null;

  if (!analysis && !profile) return null;
  return { handle, analysis, profile };
}

/** 把三类补充数据统一转成"来源"格式，复用已有的引用编号 + sanitizeCitations 校验，
 *  不需要给 wiki 报告结构新增字段——AI 综合和参考来源列表天然就会带上它们。 */
function websiteContentToSource(project, websiteContent) {
  if (!websiteContent) return null;
  const desc = [websiteContent.metaDescription, websiteContent.compressedText].filter(Boolean).join('\n').slice(0, 2000);
  return { title: '项目官网', url: websiteContent.finalUrl || project.project_live_url, description: desc };
}

function rootDataHitToSource(rootdataHit) {
  if (!rootdataHit) return null;
  return {
    title: 'RootData 收录信息',
    url: rootdataHit.rootdataUrl || 'https://www.rootdata.com',
    description: `RootData 已收录该项目${rootdataHit.introduce ? '：' + rootdataHit.introduce : '，暂无补充简介'}`,
  };
}

function twitterAnalysisToSource(twitterResult) {
  if (!twitterResult) return null;
  const a = twitterResult.analysis;
  const p = twitterResult.profile;
  const description = [
    p?.bio ? `简介：${p.bio}` : null,
    p?.followers != null ? `粉丝数：${p.followers.toLocaleString('zh-CN')}` : null,
    a?.summary,
    a?.topTopics?.length ? `高频话题：${a.topTopics.join('、')}` : null,
    a?.sentiment != null ? `整体情绪：${a.sentiment > 0.2 ? '偏正面' : a.sentiment < -0.2 ? '偏负面' : '中性'}` : null,
    a?.redFlags?.length ? `风险信号：${a.redFlags.join('；')}` : null,
    a?.greenFlags?.length ? `正面信号：${a.greenFlags.join('；')}` : null,
  ].filter(Boolean).join('\n');
  return { title: `@${twitterResult.handle} 官方推特分析`, url: `https://x.com/${twitterResult.handle}`, description };
}

/** 从 fetchTopContributors() 结果里挑出有 pedigreeSignal 的人，转成干净的顶层数组字段——
 *  跟 founderBackgroundScore 一样是结构化字段（不是塞进 sources[] 里才能读到的文本），
 *  供 fetchWikiBriefsForIds 之类的轻量缓存读取直接使用，不需要反解析文本引用。 */
function extractPedigreeHits(contributors) {
  return (contributors || [])
    .filter((c) => c.pedigreeSignal)
    .map((c) => ({ name: c.name, type: c.pedigreeSignal.type, evidence: c.pedigreeSignal.evidence }));
}

/** 把人才背景信号转成可引用来源，喂给 AI 综合创始人段落时可以直接引用编号。
 *  只呈现当事人自己公开写的信息（bio/company 原文），不做额外推断。 */
function founderPedigreeToSource(contributors, hits) {
  if (!hits.length) return null;
  const first = (contributors || []).find((c) => c.pedigreeSignal);
  return {
    title: '团队公开背景（GitHub bio/company）',
    url: first?.blog || `https://github.com/${first?.username}`,
    description: hits.map((h) => `${h.name}：${h.evidence}`).join('；'),
  };
}

/**
 * 产品 + 运营数据两段：直接拼结构化真实字段，不经过 AI，不会编造。
 * 中英文两个版本一次性都算出来（不是按当前 UI 语言算一份）——这是纯字符串拼接，
 * 零 AI 成本，两个版本都算不会增加任何开销；反过来只算当前语言那份会导致这两段
 * 内容被写进 earlybird_wiki_cache 后语言"焊死"在生成时的语言上，用户切换语言
 * 看到的还是缓存时那个语言，跟 AI 生成内容缺 _en 是同一类"看起来翻译坏了"的假象。
 */
export function buildDeterministicSections(project) {
  const product = [
    project.ai_narrative,
    project.ai_tech_zh || project.one_liner || project.description,
  ].filter(Boolean).join('\n\n') || null;
  // ai_tech_zh 是 one_liner/description 的中文翻译，英文版直接用原文，不需要单独的 _en 字段
  const productEn = [
    project.ai_narrative_en,
    project.one_liner || project.description,
  ].filter(Boolean).join('\n\n') || null;

  const opsLines = [];
  const opsLinesEn = [];
  if (project.github_url) {
    opsLines.push(`GitHub 仓库：${project.github_url}`);
    opsLinesEn.push(`GitHub repo: ${project.github_url}`);
    if (project.github_stars != null) {
      opsLines.push(`Star 数：${project.github_stars}`);
      opsLinesEn.push(`Stars: ${project.github_stars}`);
    }
    if (project.github_pushed_at) {
      const days = Math.max(0, Math.floor((Date.now() - new Date(project.github_pushed_at).getTime()) / 86400000));
      opsLines.push(`最近一次提交：${days} 天前`);
      opsLinesEn.push(`Last commit: ${days} day${days === 1 ? '' : 's'} ago`);
    }
    const contributors = project.github_top_contributors || [];
    if (contributors.length > 0) {
      const names = contributors.map(c => c.login).join('、');
      const namesEn = contributors.map(c => c.login).join(', ');
      opsLines.push(`主要贡献者：${names}`);
      opsLinesEn.push(`Top contributors: ${namesEn}`);
    }
  } else {
    opsLines.push('未解析到公开 GitHub 仓库');
    opsLinesEn.push('No public GitHub repo found');
  }
  if (project.project_live_url) {
    opsLines.push(`部署地址：${project.project_live_url}（${project.site_alive ? '当前可访问' : '当前无法访问'}）`);
    opsLinesEn.push(`Deployed at: ${project.project_live_url} (${project.site_alive ? 'currently live' : 'currently unreachable'})`);
  }
  if (project.award_date) {
    opsLines.push(`活动/收录日期：${project.award_date}`);
    opsLinesEn.push(`Event/listing date: ${project.award_date}`);
  }

  return {
    product, operatingData: opsLines.join('\n'),
    productEn, operatingDataEn: opsLinesEn.join('\n'),
  };
}

// 2026-07-09：原来在 founders/company/competitors 正文里禁止 AI 解释"排除了哪些同名
// 无关实体"，但这是一条"不要写 X"的软性指令，AI 是概率性的，实测并不能保证每次都
// 遵守（生产环境复现过：指令生效后 AI 依然在 founders 段落里写了一整段同名公司辨析）。
// 改成给 AI 一个专门的输出口子（excludedNotes 字段）承接这部分思考过程——"把心思写
// 进一个我们压根不展示的字段"比"写在正文里但装作没写"的可靠性高得多，因为这是一条
// "把 X 写进这里"的正向指令，而不是"不要在那里写 X"的负向指令。
const SOURCES_INSTRUCTIONS = `
你是 Web3 早期项目研究员。下面会给你两类信息：
1. 【已知结构化事实】—— 来自数据库的真实字段，可直接使用，不需要验证
2. 【网络搜索结果】—— 每条带编号 [n]，包含标题/链接/摘要

请只基于这两类信息生成以下内容，**创始人/公司/竞品三段的每一条具体陈述后面必须标注来源编号，如 [2]**；
如果搜索结果里确实没有相关信息，如实写"未搜索到公开信息"，**绝不允许编造姓名、公司信息或竞品**。

**同名不同实体的处理**：网络搜索经常会混入名字相同但完全不相关的公司/个人（比如同名的另一家公司、
同名的名人）。识别到这种情况后，判断和排除的思考过程写进 excludedNotes 字段，**founders/company/
competitors 三段只写和本项目真正相关的结论，绝不提及任何被排除的无关实体**。

**founders/company/competitors 三段都必须同时提供英文版本**（founders_en/company_en/competitors_en）——
跟中文版表达同样的内容（翻译，不是重新搜索/重新判断），**保留完全相同的 [n] 引用编号，编号含义不变**；
中文版写"未搜索到...信息"时，英文版对应写"No public information found"，不要留空或省略。

严格只返回 JSON：
{
  "founders": "创始人信息，带 [n] 引用；未找到则写'未搜索到公开的创始人信息'。只写结论，不要提排除了什么",
  "founders_en": "founders 的英文翻译，保留相同的 [n] 引用编号",
  "company": "公司背景信息，带 [n] 引用；未找到则写'未搜索到公开的公司信息'。只写结论，不要提排除了什么",
  "company_en": "company 的英文翻译，保留相同的 [n] 引用编号",
  "competitors": "竞品对比，带 [n] 引用；未找到则写'未搜索到明确的竞品信息'。只写结论，不要提排除了什么",
  "competitors_en": "competitors 的英文翻译，保留相同的 [n] 引用编号",
  "founderBackgroundScore": "0-100 的整数，基于 founders 段落里实际找到的信息评估创始人背景强度（过往创业经历、知名公司/机构任职、相关领域深耕年限等）；如果 founders 段落完全没有搜索到任何创始人背景信息，这里必须填 null，绝不允许在没有依据时瞎猜一个数字",
  "excludedNotes": "如果排除过同名无关实体，在这里简单记录排除了什么、为什么（内部记录，不会展示给用户）；没有排除任何内容则留空字符串"
}`;

function buildPrompt(project, searchResults) {
  const facts = [
    `项目名：${project.project_name}`,
    `来源：${project.organization}${project.program ? ` · ${project.program}` : ''}`,
    `分类：${project.tax_category || '未分类'}`,
    project.one_liner ? `一句话简介：${project.one_liner}` : null,
  ].filter(Boolean).join('\n');

  const sourcesBlock = searchResults.length > 0
    ? searchResults.map((r, i) => `[${i + 1}] ${r.title}\n${r.url}\n${r.description || ''}`).join('\n\n')
    : '（未搜索到任何相关结果）';

  return `${SOURCES_INSTRUCTIONS}\n\n【已知结构化事实】\n${facts}\n\n【网络搜索结果】\n${sourcesBlock}`;
}

/** 只保留 sources 里真实提供过的编号，丢弃 AI 可能编造的越界引用 */
export function sanitizeCitations(text, maxIndex) {
  if (!text) return text;
  return text.replace(/\[(\d+)\]/g, (m, n) => (Number(n) >= 1 && Number(n) <= maxIndex ? m : ''));
}

async function synthesizeWithAI(project, searchResults) {
  const prompt = buildPrompt(project, searchResults);
  const res = await fetch(`${AI_BASE}/api/ai/score`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ model: 'claude-opus-4-7', max_tokens: 1200, messages: [{ role: 'user', content: prompt }] }),
  }).catch(() => null);
  if (!res || !res.ok) return { founders: null, founders_en: null, company: null, company_en: null, competitors: null, competitors_en: null, founderBackgroundScore: null };

  const ai = await res.json();
  const txt = (ai.content || []).map(b => (b.type === 'text' ? b.text : '')).join('')
    .replace(/```json\s*/g, '').replace(/```\s*/g, '').trim();
  const m = txt.match(/\{[\s\S]*\}/);
  if (!m) return { founders: null, founders_en: null, company: null, company_en: null, competitors: null, competitors_en: null, founderBackgroundScore: null };

  const attempts = [
    () => JSON.parse(m[0]),
    () => JSON.parse(m[0].replace(/,\s*([}\]])/g, '$1')),
    () => JSON.parse(insertMissingCommas(m[0])),
    () => JSON.parse(escapeStrayQuotes(m[0])),
  ];
  let parsed = null;
  for (const attempt of attempts) {
    try { parsed = attempt(); break; } catch { /* try next */ }
  }
  if (!parsed) return { founders: null, founders_en: null, company: null, company_en: null, competitors: null, competitors_en: null, founderBackgroundScore: null };

  const n = searchResults.length;
  // AI 偶尔会在没有依据时也编个数字（尽管 prompt 里明确要求没找到就填 null）——
  // 这里做一次防御性校验，非法值（非整数/超出 0-100）一律当没有，不让脏数据混进动量分计算
  const rawScore = parsed.founderBackgroundScore;
  const founderBackgroundScore = Number.isInteger(rawScore) && rawScore >= 0 && rawScore <= 100 ? rawScore : null;
  return {
    founders: sanitizeCitations(parsed.founders, n),
    founders_en: sanitizeCitations(parsed.founders_en, n),
    company: sanitizeCitations(parsed.company, n),
    company_en: sanitizeCitations(parsed.company_en, n),
    competitors: sanitizeCitations(parsed.competitors, n),
    competitors_en: sanitizeCitations(parsed.competitors_en, n),
    founderBackgroundScore,
  };
}

/**
 * 生成（或读取缓存的）Early Bird 项目 wiki 式档案报告。
 * @param {object} project - earlybird_projects 表的一行（含 id/project_name/one_liner/...）
 * @param {boolean} [forceRefresh=false] - 跳过缓存强制重新生成
 * @param {(stage: string, pct: number) => void} [onProgress] - 阶段进度回调，供 UI 展示进度条
 * @param {(partial: {product, operatingData}) => void} [onPartial] - 缓存未命中时，产品/运营两段
 *   （纯结构化字段拼接，不需要等网络搜索/AI）算出来后立刻回调一次，供 UI 提前展示，不用陪着
 *   后面 10-30 秒的搜索+AI 综合一起死等。缓存命中时不会触发（本来就是秒开，没有先展示部分
 *   信息的必要）。
 * @returns {Promise<{founders, product, company, operatingData, competitors, founderBackgroundScore, founderPedigreeHits, media, twitterProfile, sources, generatedAt}>}
 */
export async function getEarlyBirdWiki(project, forceRefresh = false, onProgress, onPartial) {
  const emit = (stage, pct) => { if (onProgress) onProgress(stage, pct); };

  if (!forceRefresh && supabase) {
    emit('检查缓存…', 5);
    const { data } = await supabase
      .from('earlybird_wiki_cache')
      .select('wiki_report, generated_at')
      .eq('project_id', project.id)
      .maybeSingle();
    if (data?.wiki_report) {
      emit('完成', 100);
      return { ...data.wiki_report, generatedAt: data.generated_at, _fromCache: true };
    }
  }

  const deterministic = buildDeterministicSections(project);
  if (onPartial) onPartial(deterministic);

  // 五路补充数据源并行拉取：网络搜索/GitHub README/推特提及（已有）+ 项目官网正文 + RootData 交叉核查
  // + Surf 新闻 + GitHub 贡献者人才背景信号（新增，见 founderPedigreeToSource）
  emit('搜索网络资料、官网与新闻…', 12);
  const ghIds = parseGithubOwnerRepo(project.github_url);
  const [searchResults, websiteContent, rootdataHit, newsItems, contributors] = await Promise.all([
    collectSearchResults(project.project_name, project.github_url),
    fetchProjectWebsiteContent(project),
    fetchRootDataCrossCheck(project.project_name),
    fetchProjectNews(project.project_name),
    ghIds ? fetchTopContributors(ghIds.owner, ghIds.repo) : Promise.resolve([]),
  ]);

  // 从官网 footer 发现的链接里再拉一路：文档/博客正文 + LinkedIn 团队信息。
  // 官网自己没链接 LinkedIn（常见于纯前端渲染 SPA，footer 抓不到）时，退而找
  // 网页搜索结果里出现的 LinkedIn 公司主页链接。
  emit('抓取文档/博客与团队信息…', 40);
  const links = websiteContent?.links || {};
  const linkedinCompanyUrl = links.linkedinCompanyUrl || findLinkedInCompanyUrlInSearchResults(searchResults);
  const [{ docsContent, blogContent }, { source: linkedinSource, pedigreeHits: linkedinPedigreeHits }] = await Promise.all([
    fetchExtraWebsitePages(links),
    fetchLinkedInTeamSource(linkedinCompanyUrl),
  ]);

  emit('分析官方推特账号…', 60);
  // 官网 footer 里直接解析到的推特链接优先于文本子串匹配（不受 15000 字符截断影响，见
  // projectContext.js）；网页搜索和新闻搜索的标题/摘要也纳入候选——新闻报道经常会带上
  // 项目官方推特链接或 @handle 提及，之前只扫了网页搜索结果，漏了新闻这一路。
  const candidateTexts = [
    links.twitterUrl, websiteContent?.compressedText, docsContent?.compressedText, blogContent?.compressedText,
    ...searchResults.map(r => r.description),
    ...newsItems.map(n => `${n.title} ${n.summary || ''}`),
  ].filter(Boolean);
  const twitterResult = await analyzeProjectTwitterAccount(project.project_name, candidateTexts);
  // 合并两路人才背景信号：GitHub 贡献者 bio/company + LinkedIn 履历 priorWork。
  // 同一个人在两边都命中时会重复出现——暂不去重，两条证据都保留比丢掉一条更诚实。
  const pedigreeHits = [...extractPedigreeHits(contributors), ...linkedinPedigreeHits];

  // 补充来源放最前面（和 README 一样是"更可信的第一手/交叉核查数据"），再接原有搜索结果，
  // 引用编号统一按这个顺序生成，AI 综合 / 参考来源列表 / sanitizeCitations 三处共用同一份 allSources
  const supplementalSources = [
    websiteContentToSource(project, websiteContent),
    extraPageToSource('项目文档', links.docsUrl, docsContent),
    extraPageToSource('项目博客', links.blogUrl, blogContent),
    linkedinSource,
    rootDataHitToSource(rootdataHit),
    twitterAnalysisToSource(twitterResult),
    founderPedigreeToSource(contributors, pedigreeHits),
    ...newsToSources(newsItems),
  ].filter(Boolean);
  const allSources = [...supplementalSources, ...searchResults];

  emit('生成综合档案…', 85);
  const { founders, founders_en, company, company_en, competitors, competitors_en, founderBackgroundScore } = await synthesizeWithAI(project, allSources);

  const report = {
    founders, founders_en, ...deterministic, company, company_en, competitors, competitors_en, founderBackgroundScore,
    founderPedigreeHits: pedigreeHits, // 顶层结构化字段，供列表页轻量读取（见 fetchWikiBriefsForIds）
    media: newsItems, // 结构化新闻条目，直接展示，不经过 AI 复述
    twitterProfile: twitterResult?.profile ? { handle: twitterResult.handle, ...twitterResult.profile } : null,
    sources: allSources.map((r, i) => ({ index: i + 1, title: r.title, url: r.url })),
  };
  emit('完成', 100);

  if (supabase) {
    await supabase.from('earlybird_wiki_cache')
      .upsert({ project_id: project.id, wiki_report: report, generated_at: new Date().toISOString() }, { onConflict: 'project_id' })
      .then(() => {}, () => {}); // 缓存写入失败不影响本次展示
  }

  return { ...report, generatedAt: new Date().toISOString(), _fromCache: false };
}

// ── 生成过程跟 UI 组件生命周期解耦 ──────────────────────────────────────
// 2026-07-10：之前 getEarlyBirdWiki 是从 EarlyBirdWikiModal 的 useEffect 里直接调用的，
// 关闭面板（组件卸载）不会取消这个 fetch 本身，但 .then(setWiki) 这些状态更新全部落空——
// 重新打开同一个项目的面板，组件重新挂载又会调一次全新的生成，等于把刚才那份还没跑完
// 的（大概率也快跑完了）直接扔掉，重新触发一次 Surf/Twitter/AI 调用，纯浪费。
// 这里在模块作用域（不跟着任何组件的生命周期走）登记"这个项目 id 正在跑的生成"，
// 同一个 id 的后续调用（forceRefresh=false 时）直接接上同一个 promise + 实时进度，
// 不重新触发；调用方（UI）卸载时应该调 unsubscribeFromEarlyBirdWiki 把自己的回调
// 摘掉，避免关掉面板/切换项目后这些回调还在被调用。
// 注意：这是纯前端的"生成脱离组件生命周期"，不是"用户关掉整个浏览器标签页也继续跑"
// 的服务端后台任务——那需要把生成逻辑搬到后端常驻进程，是完全不同量级的架构改动。
const inFlightGenerations = new Map();

/**
 * @param {object} project
 * @param {boolean} forceRefresh - true 时总是发起全新生成，不复用/不登记进行中的任务
 * @param {(stage: string, pct: number) => void} [onProgress]
 * @param {(partial: object) => void} [onPartial]
 * @returns {Promise} 跟 getEarlyBirdWiki 同样的最终结果
 */
export function getOrStartEarlyBirdWiki(project, forceRefresh, onProgress, onPartial) {
  const key = project.id;
  if (!forceRefresh) {
    const existing = inFlightGenerations.get(key);
    if (existing) {
      if (onProgress) existing.progressSubs.add(onProgress);
      if (onPartial) existing.partialSubs.add(onPartial);
      if (onPartial && existing.lastPartial) onPartial(existing.lastPartial);
      if (onProgress && existing.lastProgress) onProgress(existing.lastProgress.stage, existing.lastProgress.pct);
      return existing.promise;
    }
  }

  const entry = {
    progressSubs: new Set(onProgress ? [onProgress] : []),
    partialSubs: new Set(onPartial ? [onPartial] : []),
    lastProgress: null,
    lastPartial: null,
  };
  const fanProgress = (stage, pct) => {
    entry.lastProgress = { stage, pct };
    entry.progressSubs.forEach((fn) => fn(stage, pct));
  };
  const fanPartial = (partial) => {
    entry.lastPartial = partial;
    entry.partialSubs.forEach((fn) => fn(partial));
  };
  entry.promise = getEarlyBirdWiki(project, forceRefresh, fanProgress, fanPartial)
    .finally(() => { inFlightGenerations.delete(key); });
  inFlightGenerations.set(key, entry);
  return entry.promise;
}

/** UI 组件卸载/切换项目时调用，把自己的回调从登记表里摘掉，避免继续被调用 */
export function unsubscribeFromEarlyBirdWiki(projectId, onProgress, onPartial) {
  const entry = inFlightGenerations.get(projectId);
  if (!entry) return;
  if (onProgress) entry.progressSubs.delete(onProgress);
  if (onPartial) entry.partialSubs.delete(onPartial);
}
